import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { submitAssessment, getAssignmentById, getStudentAssignments } from '../../services/assignmentService';
import api from '../../api/api';
import { format } from 'date-fns';
import '../../styles/AssignmentAssessmentPage.css';

const ASSESSMENT_LEVELS = [
  { id: 'Excellent', label: 'Excellent', color: '#2e7d32' },
  { id: 'Very Good', label: 'Very Good', color: '#388e3c' },
  { id: 'Good', label: 'Good', color: '#43a047' },
  { id: 'Pass', label: 'Pass', color: '#fb8c00' },
  { id: 'Need to Repeat', label: 'Not Ready', color: '#e53935' }
];

const AssignmentAssessmentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Extract parameters from location state
  const { studentId, studentName, assignmentDetails } = location.state || {};
  
  // State management
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [assignment, setAssignment] = useState(null);
  const [loadingAssignment, setLoadingAssignment] = useState(true);
  const [isPracticed, setIsPracticed] = useState(false);
  const [practiceData, setPracticeData] = useState(null);
  const [isPracticedLoading, setIsPracticedLoading] = useState(true);
  const [quizAttempts, setQuizAttempts] = useState(null);
  const [assignmentType, setAssignmentType] = useState('New Memorization');
  const [dueDate, setDueDate] = useState(null);
  const [markingAsPracticed, setMarkingAsPracticed] = useState(false);
  const [nextAssignments, setNextAssignments] = useState([]);
  const [loadingNextAssignment, setLoadingNextAssignment] = useState(false);

  const fetchPracticeStatus = async () => {
    setIsPracticedLoading(true); // Set loading state at the beginning
    
    if (!id || !studentId) {
      setIsPracticedLoading(false);
      return;
    }
    
    try {
      const response = await api.get(`/practice-logs?assignmentId=${id}&studentId=${studentId}`);
      const data = response.data;
      
      if (data && data.length > 0) {
        const practiceLog = data[0]; // Get the most recent log
        setIsPracticed(true);
        setPracticeData(practiceLog);
        console.log('Practice log found:', practiceLog);
      } else {
        setIsPracticed(false);
        setPracticeData(null);
      }
    } catch (error) {
      console.error('Error fetching practice status:', error);
      setIsPracticed(false);
      setPracticeData(null);
    } finally {
      setIsPracticedLoading(false); // Clear loading state at the end
    }
  };

  const fetchQuizAttempts = async (manualStudentId = null) => {
    // Use provided studentId, or the one from location.state, or exit if none available
    const effectiveStudentId = manualStudentId || studentId;

    if (!id || !effectiveStudentId) {
      console.log('Cannot fetch quiz attempts - missing assignment ID or student ID');
      return;
    }

    try {
      console.log(`Fetching quiz attempts for assignment ${id}, student ${effectiveStudentId}`);

      // Call the quiz logs API with the effective student ID
      const response = await api.get(`/quizzes/logs/${id}?studentId=${effectiveStudentId}`);

      if (response.data && response.data.success) {
        setQuizAttempts(response.data);
        console.log('Quiz attempts found:', response.data);
      } else {
        console.log('No quiz attempts found or invalid response');
        setQuizAttempts(null);
      }
    } catch (error) {
      console.error('Error fetching quiz attempts:', error);
      setQuizAttempts(null);
    }
  };

  const fetchNextAssignment = async (effectiveStudentId, currentDueDate) => {
    if (!effectiveStudentId) {
      console.log('Cannot fetch next assignment - missing student ID');
      setNextAssignments([]);
      setLoadingNextAssignment(false);
      return;
    }

    try {
      setLoadingNextAssignment(true);
      console.log(`[NextAssignment] Fetching for student ID: ${effectiveStudentId}, current assignment ID: ${id}`);
      console.log(`[NextAssignment] Current assignment due date: ${currentDueDate ? new Date(currentDueDate).toDateString() : 'No due date'}`);

      // Get all assignments for this student
      const assignments = await getStudentAssignments(effectiveStudentId);
      console.log(`[NextAssignment] Fetched ${assignments?.length || 0} total assignments for student ${effectiveStudentId}`);

      if (!assignments || assignments.length === 0) {
        console.log('[NextAssignment] No assignments found for student');
        setNextAssignments([]);
        return;
      }

      // Filter for pending/upcoming assignments (not assessed and due date > current assignment's due date)
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // Get current assignment's due date for comparison
      let currentAssignmentDate = null;
      if (currentDueDate) {
        currentAssignmentDate = new Date(currentDueDate);
        currentAssignmentDate.setHours(0, 0, 0, 0);
      }

      const upcomingAssignments = assignments.filter(a => {
        // Ensure we have required fields
        if (!a || !a._id) {
          return false;
        }

        // Verify assignment belongs to this student
        // Check if students array exists and contains the effectiveStudentId
        if (a.students && Array.isArray(a.students)) {
          const belongsToStudent = a.students.some(s => {
            const sId = typeof s === 'string' ? s : s._id;
            return sId === effectiveStudentId;
          });
          if (!belongsToStudent) {
            return false;
          }
        }

        // Not the current assignment
        if (a._id === id) {
          return false;
        }

        // Not yet assessed
        if (a.hasAssessment) {
          return false;
        }

        // Due date logic:
        // 1. Must be >= today (not overdue)
        // 2. Must be > current assignment's due date (chronologically after)
        if (a.dueDate) {
          try {
            const assignmentDueDate = new Date(a.dueDate);
            assignmentDueDate.setHours(0, 0, 0, 0);

            if (isNaN(assignmentDueDate.getTime())) {
              return false;
            }

            // Check if assignment is not overdue
            const isPending = assignmentDueDate >= today;
            if (!isPending) {
              return false;
            }

            // If current assignment has a due date, only show assignments due AFTER it
            if (currentAssignmentDate) {
              return assignmentDueDate > currentAssignmentDate;
            }

            // If current assignment has no due date, show all future assignments
            return isPending;
          } catch (e) {
            console.warn('[NextAssignment] Invalid due date format:', a.dueDate);
            return false;
          }
        }

        // If assignment has no due date, only include if current also has no due date
        return !currentAssignmentDate;
      });

      console.log(`[NextAssignment] Found ${upcomingAssignments.length} upcoming assignments after filtering`);

      // Sort by due date (earliest first)
      upcomingAssignments.sort((a, b) => {
        if (!a.dueDate) return 1; // No due date goes to the end
        if (!b.dueDate) return -1;

        try {
          const dateA = new Date(a.dueDate);
          const dateB = new Date(b.dueDate);
          return dateA - dateB;
        } catch (e) {
          console.warn('Error comparing dates:', e);
          return 0;
        }
      });

      if (upcomingAssignments.length === 0) {
        console.log('No upcoming assignments found');
        setNextAssignments([]);
        return;
      }

      // Get all assignments with the earliest due date
      const earliestDate = upcomingAssignments[0].dueDate;

      let nextBatch = [];
      if (earliestDate) {
        // Get all assignments due on the same date as the earliest
        const earliestDateObj = new Date(earliestDate);
        earliestDateObj.setHours(0, 0, 0, 0);

        nextBatch = upcomingAssignments.filter(a => {
          if (!a.dueDate) return false;
          const assignmentDate = new Date(a.dueDate);
          assignmentDate.setHours(0, 0, 0, 0);
          return assignmentDate.getTime() === earliestDateObj.getTime();
        });
      } else {
        // If earliest has no due date, just take that one
        nextBatch = [upcomingAssignments[0]];
      }

      console.log(`Found ${nextBatch.length} assignment(s) for next date:`, nextBatch);
      setNextAssignments(nextBatch);
    } catch (error) {
      console.error('Error fetching next assignment:', error);
      setNextAssignments([]);
    } finally {
      setLoadingNextAssignment(false);
    }
  };

  // State for extracted student name from assignment
  const [extractedStudentName, setExtractedStudentName] = useState(null);

  // Clear next assignments when assignment ID changes
  useEffect(() => {
    setNextAssignments([]);
    setLoadingNextAssignment(false);
  }, [id]);

  useEffect(() => {
    const fetchAssignmentDetails = async () => {
      if (!id) {
        setLoadingAssignment(false);
        return;
      }
      
      try {
        setLoadingAssignment(true);
        setIsPracticedLoading(true); // Set practice loading state
        
        // Pass student ID if available to ensure practice logs are fetched
        const data = await getAssignmentById(id, null, studentId);
        console.log('Fetched assignment details:', data);
        
        setAssignment(data);
        
        // Extract student name and ID from populated student data if available
        if (data.students && Array.isArray(data.students) && data.students.length > 0) {
          const student = data.students[0];
          
          // Handle both object format and ID format
          if (typeof student === 'object' && student.name) {
            console.log('Found student name in assignment data:', student.name);
            setExtractedStudentName(student.name);
            
            // If we don't have a studentId from location.state, extract it from the assignment
            if (!studentId && student._id) {
              console.log('Using student ID from assignment data:', student._id);
              // Store extracted student ID for quiz log fetching
              const extractedStudentId = student._id;

              // Fetch quiz logs with the extracted student ID
              fetchQuizAttempts(extractedStudentId);
              // Fetch next assignment (pass current assignment's due date)
              fetchNextAssignment(extractedStudentId, data.dueDate);
            }
          }
        }
        
        // Set assignment type and due date if available
        if (data.type) {
          setAssignmentType(data.type);
        }

        let currentDueDate = null;
        if (data.dueDate) {
          currentDueDate = new Date(data.dueDate);
          setDueDate(currentDueDate);
        }

        // Fetch next assignment for this student (if we have student ID from location.state)
        if (studentId) {
          fetchNextAssignment(studentId, data.dueDate);
        }
        
        // Check if practice logs are included in the assignment data
        if (data.practiceLogs && data.practiceLogs.length > 0) {
          console.log('Practice logs found in assignment data:', data.practiceLogs);
          setIsPracticed(true);
          setPracticeData(data.practiceLogs[0]);
        }
        
        // If there's an existing assessment, set it as selected
        if (data.assessment && data.assessment.rating) {
          const existingRating = ASSESSMENT_LEVELS.find(level => 
            level.id === data.assessment.rating
          );
          
          if (existingRating) {
            // Add isExisting flag to indicate this is an existing assessment
            setSelectedAssessment({...existingRating, isExisting: true});
            setComments(data.assessment.comments || '');
          }
        }
      } catch (error) {
        console.error('Error fetching assignment details:', error);
      } finally {
        setLoadingAssignment(false);
        setIsPracticedLoading(false); // Clear practice loading state
      }
    };

    // Create a function that orchestrates the data fetching
    const fetchAllData = async () => {
      // First fetch assignment details which includes practice logs
      // Note: fetchNextAssignment is now called inside fetchAssignmentDetails after due date is set
      await fetchAssignmentDetails();

      // Then fetch quiz attempts if we have student ID from the state
      // (if we don't, it will be handled inside fetchAssignmentDetails)
      if (studentId) {
        fetchQuizAttempts();
      }
    };

    fetchAllData();
  }, [id, studentId]);

  // Handle assessment selection
  const handleAssessmentSelect = (level) => {
    setSelectedAssessment(level);
  };

  // Submit assessment
  const handleSubmitAssessment = async () => {
    if (!selectedAssessment) {
      alert('Please select an assessment level');
      return;
    }

    setLoading(true);
    try {
      // Prepare assessment data for the API
      const assessmentData = {
        rating: selectedAssessment.id,
        comments: comments
      };

      console.log('Submitting Assessment:', assessmentData);
      
      // Call the real API
      const result = await submitAssessment(id, assessmentData);
      console.log('Assessment result:', result);
      
      // Show success alert and navigate back to the previous page
      const displayName = studentName || extractedStudentName || 'Student';
      alert(`${displayName}'s assignment has been assessed as ${selectedAssessment.label}`);
      navigate(-1);
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert(error.message || 'Failed to submit assessment');
    } finally {
      setLoading(false);
    }
  };

  if (loadingAssignment) {
    return <div className="assessment-loading">Loading assignment details...</div>;
  }

  return (
    <div className="assessment-container">
      <div className="assessment-header">
        <h1>Assignment Assessment</h1>
        <div className="header-actions">
          <button 
            onClick={() => navigate(-1)} 
            className="back-btn"
          >
            ← Back
          </button>
        </div>
      </div>
      
      <div className="assessment-content">
        {/* Student Info */}
        <div className="student-info-container">
          <div className="student-initial-circle">
            <span className="student-initial">
              {studentName ? studentName.charAt(0).toUpperCase() : 
               (extractedStudentName ? extractedStudentName.charAt(0).toUpperCase() : '?')}
            </span>
          </div>
          <div className="student-name-container">
            <h2 className="student-name">{studentName || extractedStudentName || 'Unknown Student'}</h2>
          </div>
        </div>

        {/* Assignment Details */}
        <div className="assignment-details-container">
          <div className="assignment-header">
            <h3>Assignment Details</h3>
          </div>
          <div className="assignment-content">
            <div className="assignment-type-badge">
              <span>{assignmentType}</span>
            </div>
            <div className="assignment-info-row">
              <h4 className="assignment-title">{assignmentDetails || 'Unknown Assignment'}</h4>
              <span className="assignment-due-date">
                Due: {dueDate ? format(dueDate, 'MMM dd, yyyy') : 'Not set'}
              </span>
            </div>
          </div>
        </div>

        {/* Practice Status Section */}
        {isPracticedLoading ? (
          <div className="practice-status-container practice-status-loading">
            <div className="practice-status-icon">
              <span>⟳</span>
            </div>
            <div className="practice-status-text">
              <h4>Loading practice status...</h4>
            </div>
          </div>
        ) : isPracticed && practiceData ? (
          <div className="practice-status-container">
            <div className="practice-status-icon">
              <span>✓</span>
            </div>
            <div className="practice-status-text">
              <h4>Marked as Practiced</h4>
              <p>
                {practiceData.markedAt ? format(new Date(practiceData.markedAt), 'MMM dd, yyyy HH:mm') : 'Unknown date'} 
                {' '}by {practiceData.markedBy === 'parent' ? 'Parent' : 'Student'}
              </p>
            </div>
          </div>
        ) : (
          <div className="practice-status-container practice-status-not-practiced">
            <div className="practice-status-icon practice-status-icon-not-practiced">
              <span>!</span>
            </div>
            <div className="practice-status-text">
              <h4>Not Practiced</h4>
              <p>This assignment has not yet been marked as practiced by the student.</p>
            </div>
          </div>
        )}

        {/* Quiz status container */}
        {quizAttempts && quizAttempts.quizLogs && quizAttempts.quizLogs.length > 0 && (
          <div className="quiz-status-container">
            <div className="quiz-status-icon">
              <span>✓</span>
            </div>
            <div className="quiz-status-text">
              <h4>
                Ayats Meaning Quiz Attempts: {quizAttempts.quizLogs[0].attempts.length}
              </h4>
              <p>
                Best Score: {quizAttempts.bestScore}% • Latest: {quizAttempts.latestAttempt?.score || 0}%
              </p>
            </div>
          </div>
        )}

        {/* Assessment Section */}
        <div className="assessment-section">
          <h3>Assessment</h3>
          <div className="assessment-levels">
            {ASSESSMENT_LEVELS.map((level) => (
              <button
                key={level.id}
                className={`assessment-level-button ${selectedAssessment?.id === level.id ? 'selected' : ''}`}
                style={selectedAssessment?.id === level.id ? { backgroundColor: level.color, borderColor: level.color } : {}}
                onClick={() => handleAssessmentSelect(level)}
              >
                <span 
                  className={`assessment-level-text ${selectedAssessment?.id === level.id ? 'selected-text' : ''}`}
                >
                  {level.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Comments Section */}
        <div className="comments-section">
          <h3>Comments (Optional)</h3>
          <textarea
            className="comments-input"
            placeholder="Add comments here..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={4}
          ></textarea>
        </div>

        {/* Submit Button */}
        <button
          className="submit-button"
          onClick={handleSubmitAssessment}
          disabled={loading}
        >
          {loading ? 'Submitting...' : (selectedAssessment && selectedAssessment.isExisting ? 'Update Assessment' : 'Submit Assessment')}
        </button>

        {/* Next Assignment Section */}
        {!loadingNextAssignment && nextAssignments.length > 0 && (
          <div className="next-assignment-section">
            <div className="next-assignment-header">
              <span className="next-assignment-icon">📅</span>
              <h4>
                {nextAssignments.length === 1
                  ? `Next Assignment for ${studentName || extractedStudentName || 'Student'}`
                  : `Next ${nextAssignments.length} Assignments for ${studentName || extractedStudentName || 'Student'}`}
              </h4>
            </div>
            {nextAssignments.map((assignment, index) => (
              <div key={assignment._id || index} className="next-assignment-content">
                <div className="next-assignment-title">{assignment.title || 'Untitled Assignment'}</div>
                <div className="next-assignment-details">
                  <span className="next-assignment-type">{assignment.type || 'New Memorization'}</span>
                  <span className="next-assignment-separator">•</span>
                  <span className="next-assignment-due">
                    Due: {(() => {
                      try {
                        if (!assignment.dueDate) return 'No due date';
                        const date = new Date(assignment.dueDate);
                        if (isNaN(date.getTime())) return 'No due date';
                        return format(date, 'MMM dd, yyyy');
                      } catch (e) {
                        return 'No due date';
                      }
                    })()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentAssessmentPage;